import { db } from "@/core/data/db";
import type { Attempt, Progress, Mastery } from "@/core/data/types";

/**
 * A complete portable snapshot of the user's Hippo data — every local setting and
 * content overlay (localStorage `hippo.*` keys) plus the IndexedDB tables (history,
 * progress, mastery). Used by Export/Import today, and reused by cloud sync later.
 */

export const SNAPSHOT_VERSION = 1;
const LOCAL_PREFIX = "hippo.";
const AI_KEY = "hippo.ai"; // holds the API key — excluded from export unless opted in

export interface Snapshot {
  app: "hippo";
  version: number;
  exportedAt: string;
  local: Record<string, string>;
  db: {
    attempts: Attempt[];
    progress: Progress[];
    mastery: Mastery[];
  };
}

/** Gather all local data into a snapshot. */
export async function exportSnapshot(includeAiKey = false): Promise<Snapshot> {
  const local: Record<string, string> = {};
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key || !key.startsWith(LOCAL_PREFIX)) continue;
    if (key === AI_KEY && !includeAiKey) continue;
    const value = localStorage.getItem(key);
    if (value !== null) local[key] = value;
  }

  const [attempts, progress, mastery] = await Promise.all([
    db.attempts.toArray(),
    db.progress.toArray(),
    db.mastery.toArray(),
  ]);

  return {
    app: "hippo",
    version: SNAPSHOT_VERSION,
    exportedAt: new Date().toISOString(),
    local,
    db: { attempts, progress, mastery },
  };
}

/** Trigger a download of the snapshot as a .json file. */
export async function downloadSnapshot(includeAiKey = false): Promise<void> {
  const snap = await exportSnapshot(includeAiKey);
  const blob = new Blob([JSON.stringify(snap, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `hippo-backup-${snap.exportedAt.slice(0, 10)}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function isSnapshot(x: unknown): x is Snapshot {
  const s = x as Snapshot;
  return Boolean(s) && s.app === "hippo" && typeof s.version === "number" && !!s.local && !!s.db;
}

/**
 * Replace all local data with a snapshot. Overwrites localStorage `hippo.*` keys
 * and the IndexedDB tables. The caller should reload the page afterward so the
 * in-memory stores re-hydrate.
 */
export async function importSnapshot(snap: unknown): Promise<void> {
  if (!isSnapshot(snap)) throw new Error("That doesn't look like a Hippo backup file.");
  if (snap.version > SNAPSHOT_VERSION) throw new Error("This backup was made by a newer version of Hippo.");

  // localStorage: clear existing hippo.* keys, then write the snapshot's.
  const toRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(LOCAL_PREFIX)) toRemove.push(key);
  }
  toRemove.forEach((k) => localStorage.removeItem(k));
  for (const [key, value] of Object.entries(snap.local)) localStorage.setItem(key, value);

  // IndexedDB: replace the three tables.
  await db.transaction("rw", db.attempts, db.progress, db.mastery, async () => {
    await db.attempts.clear();
    await db.progress.clear();
    await db.mastery.clear();
    if (snap.db.attempts?.length) await db.attempts.bulkPut(snap.db.attempts);
    if (snap.db.progress?.length) await db.progress.bulkPut(snap.db.progress);
    if (snap.db.mastery?.length) await db.mastery.bulkPut(snap.db.mastery);
  });
}

/** Parse a File as a snapshot and import it. */
export async function importSnapshotFile(file: File): Promise<void> {
  const text = await file.text();
  const parsed = JSON.parse(text);
  await importSnapshot(parsed);
}
